import { useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'

import { financeApi } from '@/services/finance'
import { JobTitleLink } from '@/components/jobs/job-title-link'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { formatCurrency } from '@/lib/utils'
import { formatServerDate, useTenantTimeZone } from '@/lib/tenant-date-time'
import { getServiceInstanceIdFromSearch, withServiceInstanceId } from '@/lib/service-instance'
import { Button } from '@/components/ui/button'
import { Link } from 'react-router-dom'
import type { JobInvoicePayment } from '@/types'

const toNumber = (value?: number | string | null) => {
  if (value === null || value === undefined || value === '') {
    return 0
  }

  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const getInvoiceNumberLabel = (payment: JobInvoicePayment) => {
  if (payment.invoice_number) {
    return payment.invoice_number
  }
  if (payment.invoice != null && payment.invoice !== '') {
    return `INV-${payment.invoice}`
  }
  if (payment.payment_stage === 'advance') {
    return 'Advance'
  }
  return '-'
}

const getPaymentMethodLabel = (payment: JobInvoicePayment) => {
  if (payment.payment_method_label?.trim()) {
    return payment.payment_method_label.trim()
  }
  if (payment.payment_source_label?.trim()) {
    return payment.payment_source_label.trim()
  }

  const method = payment.payment_method
  if (method && typeof method === 'object') {
    const wallet = method as {
      id?: string | number
      label?: string | null
      wallet_name?: string | null
      wallet_variant_name?: string | null
    }
    return wallet.label || wallet.wallet_name || wallet.wallet_variant_name || (wallet.id != null ? `Wallet ${wallet.id}` : '-')
  }

  if (typeof method === 'string' || typeof method === 'number') {
    return String(method)
  }

  return '-'
}

export default function InvoicePaymentsListPage() {
  const location = useLocation()
  const serviceInstanceId = getServiceInstanceIdFromSearch(location.search)
  const paymentTimeZone = useTenantTimeZone(serviceInstanceId)

  const paymentsQuery = useQuery({
    queryKey: ['finance-invoice-payments-list', serviceInstanceId ?? 'none'],
    queryFn: () => financeApi.getInvoicePayments({
      page_size: 200,
      payment_stage: 'invoice_payment',
      service_instance_id: serviceInstanceId ?? undefined,
    }),
  })

  const payments = paymentsQuery.data?.results ?? []

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Invoice Payments</CardTitle>
        </CardHeader>
        <CardContent>
          {paymentsQuery.isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-14 w-full" />
              <Skeleton className="h-14 w-full" />
              <Skeleton className="h-14 w-full" />
            </div>
          ) : payments.length === 0 ? (
            <div className="rounded-lg border border-dashed p-6 text-sm text-muted-foreground">
              No invoice payments found.
            </div>
          ) : (
            <div className="overflow-hidden rounded-lg border border-border/70">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Invoice No</TableHead>
                    <TableHead>Job Id</TableHead>
                    <TableHead>Job Title</TableHead>
                    <TableHead>Payment Date</TableHead>
                    <TableHead>Method</TableHead>
                    <TableHead>Reference</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payments.map((payment) => (
                    <TableRow key={String(payment.id)}>
                      <TableCell>{getInvoiceNumberLabel(payment)}</TableCell>
                      <TableCell>{String(payment.job_id ?? '-')}</TableCell>
                      <TableCell>
                        <JobTitleLink
                          jobId={payment.job_id}
                          title={payment.job_title}
                          serviceInstanceId={serviceInstanceId}
                        />
                      </TableCell>
                      <TableCell>{payment.payment_date ? formatServerDate(payment.payment_date, paymentTimeZone) : '-'}</TableCell>
                      <TableCell>{getPaymentMethodLabel(payment)}</TableCell>
                      <TableCell>{payment.reference || '-'}</TableCell>
                      <TableCell className="text-right font-medium">{formatCurrency(toNumber(payment.amount))}</TableCell>
                      <TableCell className="text-right">
                        {payment.invoice != null && payment.invoice !== '' ? (
                          <Button asChild variant="outline" size="sm">
                            <Link to={withServiceInstanceId(`/finance/invoices/${payment.invoice}/payments`, serviceInstanceId)}>
                              Open
                            </Link>
                          </Button>
                        ) : (
                          <span className="text-sm text-muted-foreground">-</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
